/**
 * Balloon to rule them all with a simple interface.
 * Can be used in automatic mode (click on the attachment target shows and outsideclick hides the balloon)
 * Or in manual mode (.show() and .hide() need to be called) and the attachment target is only used for the positioning of the balloon.
 * Can be used standalone or inherited from if custom DOM events for the content are necessary.
 * Outsideclick and close button can be enabled/disabled independently.
 * @module Podio.Views.SimpleBalloon
 */
define('views/lib/Podio.Views.SimpleBalloon',['require','underscore','jquery','react','react-dom','lib/backbone/Backbone.PodioView','lib/Podio.UI.Tooltips','lib/Podio.Gravity','jst!simple_balloon/_balloon','lib/jquery/jquery.removeClassByPrefix'],function(require) {

  var _ = require('underscore');
  var $ = require('jquery');
  var React = require('react');
  var ReactDOM = require('react-dom');
  var PodioView = require('lib/backbone/Backbone.PodioView');
  var tooltips = require('lib/Podio.UI.Tooltips');
  var PodioGravity = require('lib/Podio.Gravity');
  var jstBalloon = require('jst!simple_balloon/_balloon');
  require('lib/jquery/jquery.removeClassByPrefix');

  return PodioView.extend({
    FADE_SPEED: 100,
    OFFSET_FACTOR: 33,
    instances: {},        /* sits on the prototype and is shared across all instances  */
    podioEvents: {
      'click .close-option' : '_onCloseButtonClicked'
    },

    /**
     * Params list:
     * elmAttachmentTarget: jQuery object which the balloon will be positioned around and which emits click events to show/hide the balloon
     * content: JST template, a jQuery object or a JSX component with the future balloon content
     * balloonClass: a class name that will be assigned to the balllon
     * position: can be auto (will be placed wherever is place for it), horizontal/vertical (same as auto, but locked to one axis),
     *           top/bottom/left/right (fix position relative to the attachment target)
     *
     * @param {{
     *  elmAttachmentTarget: Array,
     *  content: function(),
     *  balloonClass: string,
     *  position: string
     * }} options
     */
    initialize: function(options) {
      _.bindAll(this, '_onOutsideClick', '_onAttachmentTargetClick', '_onWindowResize');

      this.elmWindow = $(window);
      this.visible = false;

      _.defaults(options, {
        hideOthers: true,                              /* Should balloon be hidden when the other balloon is shown on the page? */
        showOnClick: true,                             /* Should balloon's visibility be toggled when user clicks on the attachmentTarget? */
        stopPropagationOnClick: false,                 /* Should the click handler on the target stop propagation? */
        disableOutsideClick: false,                    /* Should we hide the balloon when user clicks outside of it? */
        closeOptionEnabled: false,                     /* Should the 'x' be displayed and clicks on it hide the balloon? */
        balloonId: _.uniqueId('simpleballoon'),        /* A custom ID for this balloon can be provided, used to identify DOM events */
        balloonAlignment: 'center',                    /* Alignment of the balloon relative to the attachment target (start|center|end) */
        arrowOffset: 0,                                /* Custom offset of the arrow relative to the one determined by the balloonAlignment. Will also influence the alignment of the balloon itself. */
        jstOptions: {},                                /* Options that should be passed into the template when it's rendered. Only valid when content is atemplate and not a jQuery object */
        jsxProps: {},                                  /* If React component is used for the content props can be provided here */
        hideArrow: false,                               /* Should hide the arrow */
        smallArrow: true,                                /* Use 7px arrow instead of the default 10px arrow */
        arrowSpacing: 10,
        alignHorizontallyWithBalloonAnchor: false /* Align balloons we can't calculate gravity for correctly on the horizontal axis */
      });

      _.extend(this, options);
      // we don't really want to persist the content, since it can change in the future
      delete this.options;

      // Make sure this is a jQuery object. Allows passing in DOM nodes
      this.elmAttachmentTarget = $(this.elmAttachmentTarget);

      this._storeInstance();

      this.gravityHelper = new PodioGravity({
        arrowSpacing: options.hideArrow ? 0 : options.arrowSpacing
      });

      if (this.showOnClick) {
        this.elmAttachmentTarget.on('click', this._onAttachmentTargetClick);
      }

      this.updateContent(options.content);
      // add the CSS class again, since Backbone is calling it before initialize
      this.elm.addClass(this._getClassName());
      // attach to DOM
      if ($('#wrapper').css('position') !== 'static') {
        $('body').append(this.elm);
      } else {
        $('#wrapper').append(this.elm);
      }

      this._super();

      if (this.openImmediately) {
        this.show();
      }
    },

    /**
     * Constructs the CSS class for the balloon
     * @returns {string}
     * @private
     */
    _getClassName: function() {
      var alignmentClass = ' alignment-' + (this.balloonAlignment);

      return 'simple-balloon new {0} {1} {2}'.format(this.balloonClass, alignmentClass, this.smallArrow ? 'small-arrow' : '');
    },

    /**
     * Will keep track of all instances of the same class, so we can hide them before showing a new one
     * @private
     */
    _storeInstance: function() {
      if (_.isUndefined(this.instances[this.balloonClass])) {
        this.instances[this.balloonClass] = [];
      }

      this.instances[this.balloonClass].push(this);
    },

    /**
     * Event handler that repositions the balloon every time the browser window is resized
     */
    _onWindowResize: _.debounce(function() {
      this._positionBalloon();
    }, 50),

    /**
     * Event handler that shows the ballon when user clicks the attachment target
     * @private
     */
    _onAttachmentTargetClick: function(e) {
      e.preventDefault();

      if (!this.isVisible()) {
        this.show();
      } else if (this.disableOutsideClick || this.stopPropagationOnClick) {
        // if outsideclick behaviour is enabled
        // it will handle hiding since elmAttachmentTarget is outside the balloon
        // unless we stop propagation
        this.hide();
      }

      if (this.stopPropagationOnClick) {
        e.stopPropagation();
        return false;
      }
    },

    /**
     * Event handler that hides the balloon when user clicks outside of it
     * @private
     */
    _onOutsideClick: function() {
      this.hide();
    },

    _onCloseButtonClicked: function(elmTarget, e) {
      e.stopPropagation();
      tooltips.hideAll();

      this.hide();
    },

    /**
     * Calculates the position for the balloon and applies it
     * @private
     */
    _positionBalloon: function() {
      var gravityPriorities = {
        auto: ['s', 'n', 'e', 'w'],
        vertical: ['n', 's'],
        horizontal: ['e', 'w']
      };
      var gravityValues = {
        left: 'e',
        right: 'w',
        top: 's',
        bottom: 'n'
      };
      var gravity, gravityPosition, calculatedGravity;

      if (/top|bottom|left|right/.test(this.position)) {
        calculatedGravity = gravityValues[this.position];
      } else if (/auto|vertical|horizontal/.test(this.position)) {
        this.gravityHelper.gravityPriorities = gravityPriorities[this.position];
        calculatedGravity = this.gravityHelper.calculateBestGravity(this.elmAttachmentTarget, this.elm);
      }

      gravity = calculatedGravity ? calculatedGravity : 'center';

      gravityPosition = this.gravityHelper.calculateElementPositionByGravity(gravity, this.elm, this.elmAttachmentTarget, this._calculateBalloonOffset(gravity, calculatedGravity ? true : false), !calculatedGravity ? this.alignHorizontallyWithBalloonAnchor : false);

      this._applyBalloonPosition(gravity, gravityPosition.position);
    },

    /**
     * Applies positioning to the balloon
     * @param {string} gravity
     * @param {Object} position
     * @private
     */
    _applyBalloonPosition: function(gravity, position) {
      this.elm.removeClassByPrefix('gravity');

      if (!this.hideArrow) {
        this.elm.addClass('gravity-' + gravity);
      }

      this.elm.css(position);
    },

    /**
     * Calculates the offset value for the balloon taking into account
     * the balloonAlignment and the arrowOffset provided by the user
     * @private
     */
    _calculateBalloonOffset: function(gravity, couldCalculateGravity) {
      // this calculation should really live inside Podio.Gravity,
      // but we didn't have time to refactor the gravity component and make sure it is still compatible with it's usages.
      var offset;

      switch (this.balloonAlignment) {
      case 'start':
        offset = this.OFFSET_FACTOR;
        break;
      case 'center':
        offset = !couldCalculateGravity && this.alignHorizontallyWithBalloonAnchor ? this.OFFSET_FACTOR : Math.floor(this._getAttachmentLength(gravity) / 2);
        break;
      case 'end':
        offset = this._getAttachmentLength(gravity) - this.OFFSET_FACTOR;
        break;
      }

      // add user's offset
      offset = offset + this.arrowOffset;

      return offset;
    },

    /**
     * Returns the height or the width of the attachmentTarget,
     * depending on the given gravity
     * @param gravity
     * @returns {integer}
     * @private
     */
    _getAttachmentLength: function(gravity) {
      if (this._isVerticalGravity(gravity)) {
        return this.elm.outerHeight(true);
      } else {
        return this.elm.outerWidth(true);
      }
    },

    /**
     * Returns whether a given gravity setting is vertical or not
     * @param gravity
     * @returns {boolean}
     * @private
     */
    _isVerticalGravity: function(gravity) {
      return (/w|e/).test(gravity);
    },

    /**
     * Shows the balloon with fade animation
     * @private
     */
    _fadeIn: function() {
      return this.elm.fadeIn(this.FADE_SPEED);
    },

    /**
     * Hides the balloon with fade animation
     * @param {integer=} delay Milliseconds to wait before the animation begins
     * @private
     */
    _fadeOut: function(delay) {
      return this.elm.fadeOut(this.FADE_SPEED);
    },

    /**
     * Will hide other balloons with the same class
     * @private
     */
    _hideOthers: function() {
      var self = this;

      _.each(this.instances[this.balloonClass], function(currentInstance) {
        if (currentInstance !== self) {
          currentInstance.hide();
        }
      });
    },

    /**
     * Renders content given as a template function or existing jQuery object (in DOM or as a fragment)
     * into the fragment of this view (this.elm). Can be used to update the content after the balloon has been placed
     * into the DOM. Can be overwritten, if necessary to support other sources of content.
     */
    updateContent: function(content) {
      var contentTemplateOptions = _.extend(this.jstOptions || {}, {
        balloonId: this.balloonId
      });
      var balloonTemplateOptions = {
        content: content,
        jstOptions: contentTemplateOptions,
        closeOptionEnabled: this.closeOptionEnabled
      };

      // is content a React component?
      if (content.isValidReactClass) {
        balloonTemplateOptions.content = null;
        this.elm.html(this.getTemplate(jstBalloon, balloonTemplateOptions));

        this.reactView = ReactDOM.render(React.createElement(content, this.jsxProps), this.elm.find('.balloon-content')[0]);
      } else if (_.isFunction(content)) {     // or a JST template?
        this.elm.html(this.getTemplate(jstBalloon, balloonTemplateOptions));
      } else if (content instanceof $) {
        this.elm.html(this.getTemplate(jstBalloon, balloonTemplateOptions));

        content.clone().show().appendTo(this.elm.find('.balloon-content'));
      }
    },

    /**
     * Returns the current visibility state of the balloon
     * @returns {Boolean}
     */
    isVisible: function() {
      return this.visible;
    },

    /**
     * Shows the balllon and binds the outsideclick
     */
    show: function() {
      var self = this;

      if (this.hideOthers) {
        this._hideOthers();
      }
      this._positionBalloon();

      this.visible = true;
      this.elmAttachmentTarget.toggleClass('balloon-visible', true);
      this.elmWindow.on('resize.{0}'.format(this.balloonId), this._onWindowResize);

      // Mouse event may be in progress, so bind outsideclick afterwards
      if (!this.disableOutsideClick) {
        _.defer(function() {
          self.elm.one('outsideclick.simpleballoon', self._onOutsideClick);
        });
      }

      var dfd = new $.Deferred();

      this.elm.fadeIn(this.FADE_SPEED, function() {
        this.trigger('simpleBalloon.{0}:balloonOpened'.format(this.balloonId));

        if (typeof this.onOpen === 'function') {
          this.onOpen(this);
        }

        dfd.resolve(this);
      }.bind(this));

      return dfd;
    },

    /**
     * Hides the balloon
     */
    hide: function() {
      // Disable outsideclick or it will trigger once elmAttachmentTarget is clicked again to show
      if (!this.disableOutsideClick) {
        this.elm.off('outsideclick.simpleballoon', this._onOutsideClick);
      }

      var dfd = new $.Deferred();

      this.elm.fadeOut(this.FADE_SPEED, function() {
        this.visible = false;
        this.elmAttachmentTarget.toggleClass('balloon-visible', false);
        this.trigger('simpleBalloon.{0}:balloonClosed'.format(this.balloonId));

        if (typeof this.onClose === 'function') {
          this.onClose(this);
        }

        dfd.resolve(this);
      }.bind(this));

      return dfd;
    },

    /**
     * Destroys the balloon
     */
    dispose: function() {
      var self = this;

      // remove from the tracked instances
      var views = this.instances[this.balloonClass];
      var currentViewIndex = _.findIndex(views, function(v) { return v === self; });

      views.splice(currentViewIndex, 1);

      // destroy React view if necessary
      if (this.reactView) {
        ReactDOM.unmountComponentAtNode(ReactDOM.findDOMNode(this.reactView).parentNode);
        delete this.reactView;
      }

      // unbind events
      this.elmWindow.off('resize.{0}'.format(this.balloonId), this._onWindowResize);
      if (this.showOnClick) {
        this.elmAttachmentTarget.off('click', this._onAttachmentTargetClick);
      }
      if (!this.disableOutsideClick) {
        this.elm.off('outsideclick.simpleballoon', this._onOutsideClick);
      }

      this._super();
    }

  });

});

